import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  CompanyAllLeavesPage,
  CompanyApplyLeave,
  CompanyLeaveManagementLayout,
  CompanyLeaveSummary,
  CompanyLeaveWorkflowRequestDetail,
  CompanyLeaveSettingsPage,
} from "@/utils/lazyLoadComponents";
import { store } from "@/store";
import { MODULE_SETTINGS_PATHS } from "./module-settings.paths";
import { companyLeaveRequestApi } from "@/services/(company)/leaveRequestApi";

// ─── Path Segments ────────────────────────────────────────────────────────────
export const LEAVES_MANAGEMENT_DYNAMIC_ID_KEY = "id";
export const LEAVES_MANAGEMENT_DYNAMIC_WORKFLOW_REQUEST_ID_KEY = "workflowRequestId";

export enum COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS {
  ROOT = "leave-management",
  ALL_LEAVES = "all-leaves",
  LEAVES_SUMMARY = "leaves-summary",
  REQUESTS_SUMMARY = "requests-summary",
  DETAILS = `:${LEAVES_MANAGEMENT_DYNAMIC_ID_KEY}`,
  APPLY = "apply",
  EDIT = "edit",
  WORKFLOW_REQUEST = "workflow-request",
  WORKFLOW_REQUEST_DETAILS = `:${LEAVES_MANAGEMENT_DYNAMIC_WORKFLOW_REQUEST_ID_KEY}`,
  // Module settings (moved out of Company Settings). Literal rather than
  // `MODULE_SETTINGS_SEGMENT`: `isolatedModules` forbids enum members
  // initialized from an imported const. Keep both in sync.
  SETTINGS = "settings",
}

// ─── Route Definition ─────────────────────────────────────────────────────────
export const companyLeavesManagementRoutes: RouteObject = {
  path: COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Leave Management" },
  HydrateFallback: Spinner,
  // No Component here — only the tab index pages get the page-level tabs layout
  children: [
    // ── Page-level tabs layout: each tab owns its own stats + table ──────────
    {
      Component: CompanyLeaveManagementLayout,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(COMPANY_LEAVES_MANAGEMENT_PATHS.ALL_LEAVES),
        },
        {
          path: COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.ALL_LEAVES,
          handle: { breadcrumb: "All Leaves" },
          Component: CompanyAllLeavesPage,
        },
      ],
    },

    // ── Module settings — a SIBLING of the tabs layout, never a child of it:
    // nesting it inside would render it under a tab bar with no matching tab.
    // Static "settings" also outranks the `:id` sibling below in React Router's
    // route ranking, so there is no collision. ──────────────────────────────
    {
      path: COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.SETTINGS,
      handle: { breadcrumb: "Settings", title: "Leave Settings" },
      Component: CompanyLeaveSettingsPage,
    },

    // ── Apply leave (no stats, no tabs) ──────────────────────────────────────
    {
      path: COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.APPLY,
      handle: { breadcrumb: "Apply" },
      Component: CompanyApplyLeave,
    },

    // ── Workflow request detail (no stats, no tabs) ──────────────────────────
    {
      path: COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.WORKFLOW_REQUEST,
      handle: { breadcrumb: "Workflow Requests" },
      children: [
        {
          path: COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.WORKFLOW_REQUEST_DETAILS,
          loader: ({ params }) => ({
            breadcrumbLabel: `Workflow Request #${params[LEAVES_MANAGEMENT_DYNAMIC_WORKFLOW_REQUEST_ID_KEY]}`,
          }),
          handle: {
            breadcrumb: (data: { breadcrumbLabel: string }) =>
              data?.breadcrumbLabel,
          },
          Component: CompanyLeaveWorkflowRequestDetail,
        },
      ],
    },

    // ── Leave summary + edit (no stats, no tabs) ─────────────────────────────
    {
      path: `${COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.DETAILS}/${COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.LEAVES_SUMMARY}`,
      loader: async ({ params }) => {
        const id = Number(params[LEAVES_MANAGEMENT_DYNAMIC_ID_KEY]);
        const result = await store.dispatch(
          companyLeaveRequestApi.endpoints.getCompanyLeaveRequestById.initiate({
            id,
          }),
        );
        const name =
          result.data?.data?.leaveRequest?.user?.firstName ?? `Leave #${id}`;
        return { breadcrumbLabel: name };
      },
      handle: {
        breadcrumb: (data: { breadcrumbLabel: string }) =>
          data?.breadcrumbLabel ?? "Leave Details",
        title: "Leave Details",
      },
      Component: CompanyLeaveSummary,
    },
    {
      path: `${COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.DETAILS}/${COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.EDIT}`,
      handle: { breadcrumb: "Edit" },
      Component: CompanyLeaveSummary,
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const COMPANY_LEAVES_MANAGEMENT_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(
    addSlashes(COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.ROOT),
  ),

  // ── Module settings (resolved in module-settings.paths.ts) ────────────────
  SETTINGS: MODULE_SETTINGS_PATHS.LEAVE_MANAGEMENT.ROOT,
  SETTINGS_VTAB_GROUP: MODULE_SETTINGS_PATHS.LEAVE_MANAGEMENT.VTAB_GROUP,
  SETTINGS_VTAB_TYPE: MODULE_SETTINGS_PATHS.LEAVE_MANAGEMENT.VTAB_TYPE,
  SETTINGS_VTAB_POLICY: MODULE_SETTINGS_PATHS.LEAVE_MANAGEMENT.VTAB_POLICY,
  SETTINGS_VTAB_BLACKOUT: MODULE_SETTINGS_PATHS.LEAVE_MANAGEMENT.VTAB_BLACKOUT,
  ALL_LEAVES: withCompanyPrefix(
    addSlashes(
      COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.ROOT,
      COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.ALL_LEAVES,
    ),
  ),
  APPLY: withCompanyPrefix(
    addSlashes(
      COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.ROOT,
      COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.APPLY,
    ),
  ),
  LEAVES_SUMMARY: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.ROOT,
        String(id),
        COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.LEAVES_SUMMARY,
      ),
    ),
  EDIT: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.ROOT,
        String(id),
        COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.EDIT,
      ),
    ),
  WORKFLOW_REQUESTS: withCompanyPrefix(
    addSlashes(
      COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.ROOT,
      COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.WORKFLOW_REQUEST,
    ),
  ),
  WORKFLOW_REQUEST_DETAIL: (workflowRequestId: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.ROOT,
        COMPANY_LEAVES_MANAGEMENT_PATH_SEGMENTS.WORKFLOW_REQUEST,
        String(workflowRequestId),
      ),
    ),
});
